import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@structural-climbing/auth'
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  Select,
  SelectItem,
} from '@structural-climbing/ui'
import { disciplineForSection } from '@structural-climbing/validation'
import { useGymDisciplines } from '../../layouts/useGymDisciplines'
import type { GymSection } from './types'

const SECTION_DISCIPLINES = [
  { key: 'bouldering', labelKey: 'routes.disciplineBouldering' },
  { key: 'rope', labelKey: 'routes.disciplineRope' },
  { key: 'other', labelKey: 'routes.disciplineOther' },
] as const

interface EditSectionModalProps {
  section: GymSection | null
  onClose: () => void
  onSaved: () => void
  apiUrl: string
}

export function EditSectionModal({ section, onClose, onSaved, apiUrl }: EditSectionModalProps) {
  const { t } = useTranslation()
  const { getAccessTokenSilently } = useAuth()
  // A bouldering-only gym has no rope sections to offer, and the reverse.
  const disciplines = useGymDisciplines()

  const [name, setName] = useState('')
  const [discipline, setDiscipline] = useState<string>('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (section) {
      setName(section.name)
      setDiscipline(section.discipline ?? '')
      setError(null)
    }
  }, [section])

  async function handleSubmit() {
    if (!section) return
    const trimmed = name.trim()
    if (!trimmed) {
      setError(t('routes.sectionNameRequired'))
      return
    }

    setLoading(true)
    setError(null)
    try {
      const token = await getAccessTokenSilently()
      const res = await fetch(`${apiUrl}/gym-sections/${section.id}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: trimmed, discipline: discipline || null }),
      })
      if (!res.ok) {
        const json = (await res.json()) as { error?: string }
        throw new Error(
          json.error === 'sectionDisciplineHasRoutes'
            ? t('routes.sectionDisciplineHasRoutes')
            : (json.error ?? t('routes.errorUpdateSection')),
        )
      }
      onSaved()
      onClose()
    } catch (e) {
      setError(e instanceof Error ? e.message : t('routes.errorUpdateSection'))
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal
      scrollBehavior="inside"
      isOpen={!!section}
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
    >
      <ModalContent>
        {() => (
          <>
            <ModalHeader>{t('routes.editSectionTitle')}</ModalHeader>
            <ModalBody>
              <div className="flex flex-col gap-4">
                <Input
                  label={t('routes.sectionName')}
                  value={name}
                  onValueChange={setName}
                  isRequired
                />
                <Select
                  label={t('routes.sectionDiscipline')}
                  selectedKeys={discipline ? [discipline] : []}
                  onSelectionChange={(keys) => {
                    const val = Array.from(keys)[0]
                    setDiscipline(val ? String(val) : '')
                  }}
                >
                  {SECTION_DISCIPLINES.filter((d) =>
                    disciplines.includes(disciplineForSection(d.key)),
                  ).map((d) => (
                    <SelectItem key={d.key}>{t(d.labelKey)}</SelectItem>
                  ))}
                </Select>
                {error && <p className="text-sm text-danger">{error}</p>}
              </div>
            </ModalBody>
            <ModalFooter>
              <Button variant="flat" onClick={onClose} isDisabled={loading}>
                {t('common.cancel')}
              </Button>
              <Button color="primary" onClick={handleSubmit} isLoading={loading}>
                {t('routes.save')}
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  )
}
